import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { DEFAULT_CATEGORY_THRESHOLD } from '@/app/utils';

import { type GroupId, type ViewMode, ViewModeOptions } from './types';
import { getInitialViewState, type ViewState } from '@/views/map/deckglUtils';
import type { WebMercatorViewportOptions } from '@/views/map/types';
import {
  GroupAnalysisType,
  NetworkGroupType,
} from '@/generated/types/navigator/types.gen';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import type { NetworkGroupData as NetworkGroup } from '@/generated/types/navigator/types.gen';
import { fetchNetworkGroups } from '@/features/networkGroupSlice';
import { isInProjectUrl } from '@/helpers/url';
import { hasNetworkGroupType, UNGROUPED } from '@/helpers/networkGroups';
import type { Feature, Geometry } from 'geojson';

export interface SelectedItem {
  object?: Feature<
    Geometry,
    {
      displayName?: string | null;
      name?: string;
      group?: string;

      // Pole-specific properties
      poleID?: string;
      displayHeightInFt?: number;
      structureType?: string;
      structureWidthInFt?: number;

      // Tree-specific properties
      dn?: number;
      type?: string;

      // Hazard tree specific properties
      bufferInFt?: number | null;
      distInFt?: number;
      treeType?: string;

      // Red flag warning properties
      title?: string;
      coveredCircuits?: number;
      coveredDistance?: number;
      issued?: string;
      start?: string;
      end?: string;
      nwsLink?: string;
    } | null
  >;
  layer?: { id: string };
  path?: string;
  coordinate?: number[];
  id?: string;
}

export type GroupDetailTab = 'summary' | 'spans';

export interface AnalysisQueryState {
  isListView: boolean;
  groupDetailTab: GroupDetailTab;
  selectedItem: SelectedItem;
  isFilterForm: boolean;
  isInspectMode: boolean;
  currentViewMode: ViewMode;
  selectedAnalysis: GroupAnalysisType;
  selectedNetworkGroup: NetworkGroup | null;
  /** the name of the group item selected (not network group) */
  activeGroupName: string | null;
  encroachmentThresholds: number[];
  priorityThresholds: number[];
  wildfirePriorityThresholds: number[];
  criticalTreesThresholds: number[];
  clearThresholds: number[];
  /** the network group associated to the "circuit" network group */
  circuitNetworkGroupId: GroupId | null;
  /** the network group associated to the "span" network group */
  spanNetworkGroup: NetworkGroup | null;
  /** Sets the group config */
  planningGroup: GroupId | null;
  /** the id for the network group displayed on the map */
  mapNetworkGroupId: GroupId | null;
  /** the bounding box of the filtered data */
  filterBoundingBox: number[] | null;
  viewState: ViewState;
  // Snapshot of viewState taken when the user drills into a group from the
  // list, used to restore the prior viewport on in-app Back. null when no
  // drill is active.
  viewStateBeforeDrill: WebMercatorViewportOptions | null;
  mapState: ViewState;
  clientIsLoaded: boolean;
  /** Increment this to force MVT cache refresh dependent queries */
  mvtCacheCounter: number;
}

const initialState: AnalysisQueryState = {
  isListView: false,
  groupDetailTab: 'summary',
  selectedItem: {},
  isFilterForm: false,
  isInspectMode: true,
  currentViewMode: { mode: ViewModeOptions.UNINITIALIZED, modeDetails: null },
  selectedAnalysis: GroupAnalysisType.ENCROACHMENT,
  selectedNetworkGroup: null,
  activeGroupName: null,
  encroachmentThresholds: [],
  priorityThresholds: [],
  wildfirePriorityThresholds: [],
  criticalTreesThresholds: DEFAULT_CATEGORY_THRESHOLD,
  clearThresholds: [],
  spanNetworkGroup: null,
  circuitNetworkGroupId: null,
  planningGroup: null,
  mapNetworkGroupId: null,
  filterBoundingBox: null,
  viewState: getInitialViewState(),
  viewStateBeforeDrill: null,
  // mapState tracks the 'internal' state of the DeckGL component
  // it must be separate from viewState, which is used to calculate
  // the initial starting point of the DeckGL component
  // We'll only *use* mapState to preserve key internal changes when
  // change out big parts of the app -- toggling a scan or going back
  // and forth to the dashboard/etc.
  mapState: getInitialViewState(),
  clientIsLoaded: false,
  mvtCacheCounter: 0,
};

const analysisMapSlice = createSlice({
  name: 'analysisMap',
  initialState: initialState,
  reducers: {
    updateSelectedAnalysis: (
      state,
      action: PayloadAction<GroupAnalysisType>
    ) => {
      state.selectedAnalysis = action.payload;
    },
    updateSelectedNetworkGroup: (
      state,
      action: PayloadAction<NetworkGroup | null>
    ) => {
      state.selectedNetworkGroup = action.payload;
    },
    updateMapNetworkGroupId: (state, action: PayloadAction<GroupId | null>) => {
      state.mapNetworkGroupId = action.payload;
    },
    updateViewState: (
      state,
      action: PayloadAction<Partial<WebMercatorViewportOptions>>
    ) => {
      state.viewState = {
        ...state.viewState,
        ...action.payload,
      };
    },
    saveViewStateBeforeDrill: (
      state,
      action: PayloadAction<WebMercatorViewportOptions>
    ) => {
      state.viewStateBeforeDrill = { ...action.payload };
    },
    restoreViewStateBeforeDrill: (state) => {
      const saved = state.viewStateBeforeDrill;
      if (saved) {
        state.viewState = { ...state.viewState, ...saved };
        state.viewStateBeforeDrill = null;
      }
    },
    setListView: (state, action: PayloadAction<boolean>) => {
      state.isListView = action.payload;
    },
    setGroupDetailTab: (state, action: PayloadAction<GroupDetailTab>) => {
      state.groupDetailTab = action.payload;
    },
    selectItem: (state, action: PayloadAction<SelectedItem>) => {
      state.selectedItem = action.payload;
    },
    clearItem: (state) => {
      state.selectedItem = {};
    },
    openFilterForm: (state) => {
      state.isFilterForm = true;
    },
    closeFilterForm: (state) => {
      state.isFilterForm = false;
    },
    updateInspectMode: (state, action: PayloadAction<boolean>) => {
      TagManager.dataLayer({
        dataLayer: {
          event: GoogleEvents.inspectMode,
          status: action.payload ? 'on' : 'off',
        },
      });

      state.isInspectMode = action.payload;
    },
    setActiveGroupName: (state, action: PayloadAction<string | null>) => {
      state.activeGroupName = action.payload;
    },
    setEncroachmentThresholds: (state, action: PayloadAction<number[]>) => {
      state.encroachmentThresholds = action.payload;
    },
    setPriorityThresholds: (state, action: PayloadAction<number[]>) => {
      state.priorityThresholds = action.payload;
    },
    setWildfirePriorityThresholds: (state, action: PayloadAction<number[]>) => {
      state.wildfirePriorityThresholds = action.payload;
    },
    setCriticalTreesThresholds: (state, action: PayloadAction<number[]>) => {
      state.criticalTreesThresholds = action.payload;
    },
    setClearThresholds: (state, action: PayloadAction<number[]>) => {
      state.clearThresholds = action.payload;
    },
    setFilterBoundingBox: (state, action: PayloadAction<number[]>) => {
      state.filterBoundingBox = action.payload;
    },
    /** As we add more modes, we can add them here. */
    setCurrentViewMode: (state, action: PayloadAction<ViewMode>) => {
      const newMode = action.payload.mode;
      state.currentViewMode.mode = newMode;
      if (
        newMode === ViewModeOptions.CUSTOM_LIST_OVERVIEW ||
        newMode === ViewModeOptions.LEGACY
      ) {
        state.currentViewMode.modeDetails = null; // No details for overview or legacy
      } else if (isInProjectUrl(action.payload)) {
        state.currentViewMode.modeDetails = action.payload.modeDetails;
      }
    },
    updateMapState: (
      state,
      action: PayloadAction<Partial<WebMercatorViewportOptions>>
    ) => {
      state.mapState = {
        ...state.mapState,
        ...action.payload,
      };
      state.clientIsLoaded = true;
    },
    incrementMvtCacheCounter: (state) => {
      state.mvtCacheCounter += 1;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(isAnyOf(fetchNetworkGroups), (state, action) => {
      if (action.payload !== undefined) {
        const spanNetworkGroup =
          action.payload.find(
            (group: NetworkGroup) => group.name === 'level3'
          ) ?? UNGROUPED;

        const circuitNetworkGroup = action.payload.find(
          (group: NetworkGroup) => group.name === 'circuit'
        );

        const planningGroup = action.payload.find((group: NetworkGroup) =>
          hasNetworkGroupType(group, NetworkGroupType.PLANNING)
        );

        return {
          ...state,
          spanNetworkGroup:
            spanNetworkGroup.id === state.spanNetworkGroup?.id
              ? state.spanNetworkGroup
              : spanNetworkGroup,
          circuitNetworkGroupId: circuitNetworkGroup?.id ?? null,
          planningGroup: planningGroup?.id ?? null,
        };
      }
      return state;
    });
  },
});

export const {
  setListView,
  setGroupDetailTab,
  selectItem,
  clearItem,
  setActiveGroupName,
  openFilterForm,
  closeFilterForm,
  updateInspectMode,
  updateSelectedAnalysis,
  updateSelectedNetworkGroup,
  updateViewState,
  saveViewStateBeforeDrill,
  restoreViewStateBeforeDrill,
  updateMapNetworkGroupId,
  setEncroachmentThresholds,
  setPriorityThresholds,
  setWildfirePriorityThresholds,
  setCriticalTreesThresholds,
  setClearThresholds,
  setFilterBoundingBox,
  setCurrentViewMode,
  updateMapState,
  incrementMvtCacheCounter,
} = analysisMapSlice.actions;

export default analysisMapSlice.reducer;
